<script setup lang="ts">
  import { computed, useSlots } from 'vue'
  import { cva } from 'class-variance-authority'
  import type { ISbRichtext } from 'storyblok-js-client'
  import renderRichText from '~/utils/renderSbRichText'

  type ColorT = 'primary' | 'ghost'
  type PositionT = 'top' | 'center' | 'bottom'
  type MobileDirection = 'row' | 'column'

  interface Props {
    text?: ISbRichtext
    title?: string
    color?: ColorT
    bg?: ColorT
    fullText?: boolean
    fullTitle?: boolean
    actionPosition?: PositionT
    illustrationPosition?: PositionT
    mobileDirection?: MobileDirection
  }

  const props = withDefaults(defineProps<Props>(), {
    color: 'primary',
    bg: 'primary',
    fullText: false,
    fullTitle: false,
    actionPosition: 'top',
    illustrationPosition: 'top',
    mobileDirection: 'row',
    text: undefined,
    title: ''
  })

  const bannerBase = computed(() =>
    cva('flex flex-col justify-between rounded-lg md:flex-row gap-8', {
      variants: {
        color: {
          primary: 'text-bt-ui-dark-blue',
          ghost: ' text-bt-ui-secondary-text'
        },
        background: {
          primary: 'bg-bt-ui-bg p-6',
          ghost: 'bg-bt-ui-white'
        },
        density: {
          hasText: 'space-y-4 md:space-y-0 md:space-x-6',
          noText: 'pt-10 pb-8',
          hasTitleSlot: ''
        }
      }
    })({
      color: props.color,
      background: props.bg,
      density: slots.title ? 'hasTitleSlot' : props.text ? 'hasText' : 'noText'
    })
  )

  const bannerContentBase = computed(() =>
    cva('flex items-center', {
      variants: {
        mobileDirection: {
          row: 'flex-row gap-6',
          column: 'flex-col md:flex-row gap-4'
        }
      }
    })({
      mobileDirection: props.mobileDirection
    })
  )

  const actionBase = computed(() =>
    cva('', {
      variants: {
        position: {
          top: 'md:self-start',
          center: 'md:self-center',
          bottom: 'md:self-end'
        }
      }
    })({
      position: props.actionPosition
    })
  )

  const illustrationBase = computed(() =>
    cva('', {
      variants: {
        position: {
          top: 'self-start',
          center: 'self-center',
          bottom: 'self-end'
        }
      }
    })({
      position: props.illustrationPosition
    })
  )

  const slots = useSlots()
</script>

<template>
  <div :class="bannerBase">
    <div :class="bannerContentBase">
      <div
        v-if="slots.illustration"
        :class="illustrationBase"
      >
        <slot name="illustration" />
      </div>
      <div>
        <h4
          v-if="title && !slots.title"
          class="md:flex-grow"
          :class="{ 'line-clamp-1': !fullTitle, 'mb-4': !!text }"
        >
          {{ title }}
        </h4>
        <slot
          v-else
          name="title"
          :class-name="[{ 'line-clamp-1': !fullTitle, 'mb-4': !!text }, 'md:flex-grow', 'text-bt-ui-primary-text']"
        />
        <p
          v-if="typeof text === 'string'"
          class="font-normal text-body-1 md:flex-grow"
          :class="{ 'line-clamp-2 text-ellipsis': !fullText }"
        >
          {{ text }}
        </p>
        <p
          v-else
          class="font-normal text-body-1 md:flex-grow"
          :class="{ 'line-clamp-2 text-ellipsis': !fullText }"
          v-html="renderRichText(text)"
        />
      </div>
    </div>
    <slot
      name="action"
      :class-name="actionBase"
    />
  </div>
</template>
